import { toDateOnly } from "./date-utils";
import { EventType } from "./enums";

/**
 * Every calendar day an event spans, from its start date through its end date
 * inclusive, as `YYYY-MM-DD` strings.
 *
 * Days are derived from the event's date range rather than stored, so there is
 * no separate table to keep in sync. Iteration is done entirely on the UTC
 * calendar, matching the project's noon-UTC date-only convention — it cannot
 * roll to a different day in any timezone.
 *
 * Returns an empty array when either bound is missing or the range is inverted.
 */
export function enumerateEventDays(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string[] {
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);
  if (!start || !end) return [];

  const days: string[] = [];
  const cursor = new Date(`${start}T00:00:00.000Z`);
  const last = new Date(`${end}T00:00:00.000Z`);
  while (cursor.getTime() <= last.getTime()) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

/** Event types that run overnight and can therefore sell a single-day pass. */
const CAMPOUT_EVENT_TYPES: EventType[] = [EventType.Badger, EventType.PioneerRun];

export interface DayPassEligibility {
  event_type: EventType;
  start_date?: string | null;
  end_date?: string | null;
  day_pass_cost?: number | null;
}

/**
 * Whether an event may be registered for one day at a time.
 *
 * Requires an overnight event type, a day-pass price, and a span of two or more
 * days — a single-day event has no "which day" to choose.
 *
 * The price is checked against null rather than for truthiness on purpose: a $0
 * day pass (a free day on an otherwise paid event) is valid configuration, and a
 * truthiness check would silently hide it.
 */
export function isDayPassAvailable(event: DayPassEligibility): boolean {
  if (!CAMPOUT_EVENT_TYPES.includes(event.event_type)) return false;
  if (event.day_pass_cost == null) return false;
  return enumerateEventDays(event.start_date, event.end_date).length >= 2;
}
